<template>
  <panel class="case-medications" color="blue-1" title="Medications/Fluids" scroll-mode="content" padding-mode="top-only" data-testid="medications-page-container">
    <Tabs ref="tabs" class="case-medications-tabs" data-testid="tabs-container" @input="changeActiveTab">
      <TabItem name="Medication List">
        <case-medication-list
          class="medications-subpanel"
          :medications="medications"
          :dosage-forms="caseConf.dosageForms"
          :custom-medications="caseConf.customMedications"
          :custom-groups="caseConf.customMedGroups"
          :readonly="readonly"
          :loading="loading || loadingMedication != null"
          :selected-medication="state.selectedMedication"
          data-testid="medications-list-container"
          @select="selectMedication"
          @add="addMedication"
          @updateCase="updateCase"
        />
      </TabItem>
      <TabItem name="Fluid List">
        <case-fluid-list
          class="medications-subpanel medications-subpanel-fluids"
          :case-fluids="caseConf.fluids"
          :custom-fluids="caseConf.customFluids"
          :readonly="readonly"
          :selected-fluid="state.selectedFluid"
          data-testid="fluids-list-container"
          @select="selectFluid"
          @updateCase="updateCase"
        />
      </TabItem>
    </Tabs>
    <collapsable-panel
      v-if="state.selectedMedication"
      class="medications-subpanel"
      title="Medication Details"
      expand-to-right
      :collapsed="state.detailsCollapsed"
      :icon="require('@/assets/icons/medications.svg')"
      data-testid="medication-details-container"
      @expand="expandDetails"
    >
      <template slot="buttons">
        <md-menu>
          <md-button md-menu-trigger class="copy-button md-icon-button md-dense" :disabled="readonly" aria-label="Copy" data-testid="copy-med-menu-button">
            <md-icon>content_copy</md-icon>
          </md-button>
          <md-menu-content data-testid="copy-menu-container">
            <md-menu-item aria-label="Copy Dosage Form" data-testid="copy-dosage-form-option" @click="copyMedication">Copy Dosage Form</md-menu-item>
            <md-menu-item aria-label="Copy Group" data-testid="copy-group-option" @click="copyGroup">Copy Group</md-menu-item>
          </md-menu-content>
        </md-menu>
        <md-button
          v-if="state.selectedMedication.isCustom"
          class="md-icon-button md-dense"
          aria-label="Delete Dosage Form"
          data-testid="delete-dosage-form-button"
          @click="removeMedication"
        >
          <md-icon>delete_outline</md-icon>
        </md-button>
      </template>
      <case-medication-details
        :medication="state.selectedMedication"
        :dosage-form="dosageForm"
        :custom-groups="caseConf.customMedGroups"
        :readonly="readonly || !dosageForm"
        :medications="medications"
        @save="saveMedication"
        @updateDosageForm="df => updateDosageForm(df)"
        @add="addMedication"
        @remove="removeMedication"
        @updateCase="updateCase"
      />
    </collapsable-panel>
    <collapsable-panel
      v-if="state.selectedMedication"
      class="medications-subpanel effect-chain"
      title="Effect Chain"
      collapsed-color="#313136"
      expand-to-left
      :collapsed="state.effectChainCollapsed"
      :icon="require('@/assets/icons/effect-chain.svg')"
      data-testid="effect-chain-container"
      @expand="expandEffectChain"
    >
      <case-medication-effect-chain-container
        v-if="state.selectedMedication.isCustom ? true : medication"
        class="medications-subpanel"
        :case-conf="caseConf"
        :medication="state.selectedMedication.isCustom ? state.selectedMedication : medication"
        :dosage-form="dosageForm"
        :readonly="readonly || !dosageForm"
        @save="saveMedication"
        @updateDosageForm="saveDosageForm"
      />
    </collapsable-panel>
    <collapsable-panel
      v-if="state.selectedFluid"
      class="medications-subpanel"
      title="Fluid Details"
      expand-to-right
      data-testid="fluid-details-container"
      :icon="require('@/assets/icons/fluids.svg')"
    >
      <template slot="buttons">
        <md-button
          class="md-icon-button md-dense"
          :disabled="readonly"
          aria-label="Copy"
          data-testid="copy-fluid-button"
          @click="copyFluid"
        >
          <md-icon>content_copy</md-icon>
        </md-button>
        <md-button
          v-if="state.selectedFluid.isCustom"
          class="md-icon-button md-dense"
          :disabled="readonly"
          aria-label="Delete Fluid"
          data-testid="delete-fluid-button"
          @click="removeFluid"
        >
          <md-icon>delete_outline</md-icon>
        </md-button>
      </template>
      <case-fluid-details
        :fluid="state.selectedFluid"
        :case-fluids="caseConf.fluids"
        :readonly="readonly"
        @updateCase="updateCase"
      />
    </collapsable-panel>
  </panel>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import { Tabs, TabItem } from 'vue-material-tabs';

import Panel from '../../common/Panel';
import CaseMedicationList from './CaseMedicationList';
import CaseFluidList from '../CaseFluids/CaseFluidList';
import CaseMedicationDetails from './CaseMedicationDetails';
import CaseFluidDetails from '../CaseFluids/CaseFluidDetails';
import CaseMedicationEffectChainContainer from './CaseMedicationEffectChainContainer';
import CollapsablePanel from './CollapsablePanel';
import { copyMedication, copyGroup } from '../../../utils/customMedications';
import { copyFluid } from '../../../utils/customFluids';

export default {
  name: 'case-medications',
  components: {
    Panel,
    CaseMedicationList,
    CaseFluidList,
    CaseMedicationDetails,
    CaseFluidDetails,
    CaseMedicationEffectChainContainer,
    CollapsablePanel,
    Tabs,
    TabItem,
  },
  props: {
    caseConf: {
      type: Object,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    medications: {
      type: Array,
      default: () => [],
    },
    medication: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    loadingMedication: {
      type: String,
      default: null,
    },
    saving: {
      type: Boolean,
      default: false,
    },
    navigation: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      state: {
        activeTabIndex: 0,
        selectedMedication: null,
        selectedFluid: null,
        detailsCollapsed: false,
        effectChainCollapsed: true,
      }
    };
  },
  computed: {
    dosageForm() {
      return this.caseConf.dosageForms.find(
        df => df.medicationId === this.state.selectedMedication.id
      );
    },
  },
  watch: {
    saving(value) {
      if (value) {
        this.$emit('saving');
      } else {
        this.$emit('saved');
      }
    },
    navigation: {
      handler() {
        this.navigationHandler();
      },
      deep: true
    },
  },
  mounted() {
    this.$emit('setQueryContext', {
      type: 'case',
      id: this.caseConf.id,
    });
    this.$emit('fetch');
    this.navigationHandler();
  },
  methods: {
    navigationHandler() {
      // If navigation is truthy, then the error/warning menu was clicked
      if (this.navigation && this.navigation.params) {
        if (this.navigation.params.field === 'initialMedications') {
          this.$eventHub.$emit('UI::OPEN_ON_AT_START_PANEL', this.navigation);
        }
        if (this.navigation.params.medicationId) {
          const med = this.medications.find(m => m.id === this.navigation.params.medicationId);
          this.selectMedication(med);
        }
      }
    },
    updateCase(caseUpdates) {
      this.$emit('update', caseUpdates);
    },
    updateDosageForm(dosageForm) {
      const dosageForms = this.caseConf.dosageForms;
      const index = dosageForms.findIndex(df => df.id == dosageForm.id);
      if (index >= 0) {
        dosageForms[index] = dosageForm;
        this.updateCase({ dosageForms });
      }
    },
    changeActiveTab(index) {
      if (index !== this.state.activeTabIndex) {
        this.state.activeTabIndex = index;
        this.state.selectedMedication = null;
        this.state.selectedFluid = null;
      }
    },
    selectMedication(med) {
      this.state.selectedMedication = {
        ...med,
        isCustom: !!(this.caseConf.customMedications || []).find(cm => cm.key === med.id)
      };
      if (!this.state.selectedMedication.isCustom) {
        this.$emit('fetchById', med.id);
      }
      this.state.selectedFluid = null;
      this.$analytics.event('Authoring Tool', 'medications_name_selected', med.name.en);
    },
    selectFluid(fluid) {
      this.state.selectedFluid = {
        ...fluid,
        isCustom: !!(this.caseConf.customFluids || []).find(cf => cf.key === fluid.key)
      };
      this.state.selectedMedication = null;
      this.$analytics.event('Authoring Tool', 'fluids_name_selected', fluid.abbreviation.en);

    },
    saveMedication(med, callback) {
      this.$emit('save', med, callback);
    },
    saveDosageForm(dosageForm) {
      const dfIndex = this.caseConf.dosageForms.findIndex(df => df.id === dosageForm.id);
      if (dfIndex >= 0) {
        const updatedDosageForms = cloneDeep(this.caseConf.dosageForms)
        updatedDosageForms.splice(dfIndex, 1, dosageForm);
        this.updateCase({ dosageForms: updatedDosageForms });
      }
    },
    addMedication(med, successCallback, errorCallback) {
      this.$emit('add', med, (newMedication) => {
        if (successCallback) {
          successCallback(newMedication);
        }
        this.$emit('fetch');
        this.selectMedication(newMedication);
      }, errorCallback);
    },
    copyMedication() {
      const medId = this.state.selectedMedication.id;
      const groupKey = this.state.selectedMedication.group;
      const caseUpdates = copyMedication(this.caseConf, this.medications, medId, groupKey)
      this.updateCase(caseUpdates);
      this.selectMedication(caseUpdates.customMedications[caseUpdates.customMedications.length - 1]);
    },
    copyGroup() {
      const existingGroupKey = this.state.selectedMedication.group;
      const caseUpdates = copyGroup(this.caseConf, this.medications, existingGroupKey);
      this.updateCase(caseUpdates);
      this.$nextTick(() => {
        const newGroupKey = caseUpdates.customMedGroups[caseUpdates.customMedGroups.length - 1].key;
        this.$eventHub.$emit('SWITCHTREE::SCROLL_TO', newGroupKey);
      });
    },
    removeMedication() {
      const caseUpdates = {};
      const medKey = this.state.selectedMedication.id;

      const medIndex = this.caseConf.customMedications.findIndex(cm => cm.key === medKey);
      if (medIndex >= 0) {
        const updatedCustomMeds = cloneDeep(this.caseConf.customMedications);
        updatedCustomMeds.splice(medIndex, 1);
        caseUpdates.customMedications = updatedCustomMeds;
      }

      const dosageIndex = this.caseConf.dosageForms.findIndex(df => df.medicationId === medKey);
      if (dosageIndex >= 0) {
        const updatedDosageForms = cloneDeep(this.caseConf.dosageForms);
        updatedDosageForms.splice(dosageIndex, 1);
        caseUpdates.dosageForms = updatedDosageForms;
      }

      if (Object.keys(caseUpdates).length > 0) {
        this.updateCase(caseUpdates);
        this.state.selectedMedication = null;
      }
    },
    copyFluid() {
      const updates = copyFluid(this.caseConf, this.state.selectedFluid);
      this.updateCase(updates);
    },
    removeFluid() {
      const { key, isCustom } = this.state.selectedFluid;
      const updates = {};
      updates.fluids = this.caseConf.fluids.filter(fluid => fluid.key !== key);
      if (isCustom) {
        updates.customFluids = this.caseConf.customFluids.filter(cf => cf.key !== key);
      }
      this.updateCase(updates);
      this.state.selectedFluid = null;
    },
    expandDetails() {
      this.state.detailsCollapsed = false;
      this.state.effectChainCollapsed = true;
    },
    expandEffectChain() {
      this.state.detailsCollapsed = true;
      this.state.effectChainCollapsed = false;
    },
  },
};
</script>

<style scoped>
.case-medications >>> .panel-content {
  display: flex;
  overflow-y: hidden;
}

.medications-subpanel {
  height: calc(100% - 8px);
}

.medications-subpanel-fluids {
  height: calc(100% - 58px);
}

.case-medications-tabs,
.medications-subpanel:not(:last-child) {
  margin-right: 20px;
}

.medications-subpanel.effect-chain {
  align-self: flex-start;
}

.tabs {
  width: auto !important;
}

.tabs >>> .tabs__content {
  height: 100%;
}

.tabs__content .vmt__tab-item {
  height: 100%;
  background-color: #F3F3F3;
}
</style>
